import { Link } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CheckIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  PaperClipIcon,
  PlusIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  PortalOverviewData,
  PortalOverviewDeliveryStatus,
  PortalOverviewPhase,
} from './portalOverviewMock';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function deliveryStatusClass(status: PortalOverviewDeliveryStatus): string {
  if (status === 'inProgress') return 'bg-primary-50 text-primary-800';
  if (status === 'done') return 'bg-emerald-50 text-emerald-800';
  return 'bg-secondary-100 text-secondary-700';
}

function deliveryStatusLabel(status: PortalOverviewDeliveryStatus): string {
  if (status === 'inProgress') return 'Em andamento';
  if (status === 'done') return 'Concluído';
  return 'Pendente';
}

function phaseStatusLabel(status: PortalOverviewPhase['status']): string {
  if (status === 'done') return 'Concluído';
  if (status === 'current') return 'Em andamento';
  return 'Pendente';
}

function ProgressRing({ percent }: { percent: number }) {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg className="h-20 w-20 -rotate-90" viewBox="0 0 80 80" aria-hidden>
        <circle cx="40" cy="40" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="8" />
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-primary-500"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-secondary-900">
        {percent}%
      </span>
    </div>
  );
}

type PortalProjectOverviewProps = {
  overview: PortalOverviewData;
};

export function PortalProjectOverview({ overview }: PortalProjectOverviewProps) {
  const milestonePercent = Math.round((overview.milestonesDone / overview.milestonesTotal) * 100);

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_16.5rem]">
      <div className="min-w-0 space-y-5">
        <section className="flex gap-4 rounded-2xl border border-primary-100 bg-primary-50/70 px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <ArrowTrendingUpIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-secondary-900">
              Resumo do projeto:{' '}
              <span className="font-semibold text-primary-800">{overview.summaryTitle}</span>
            </p>
            <p className="mt-1 text-sm leading-relaxed text-secondary-700">{overview.summaryBody}</p>
            <button type="button" className="mt-2 text-sm font-semibold text-primary-700 hover:underline">
              Ver detalhes →
            </button>
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <article className="rounded-2xl border border-secondary-200 bg-white p-4">
            <p className="text-sm font-medium text-secondary-600">Progresso geral</p>
            <div className="mt-3 flex items-center gap-3">
              <ProgressRing percent={overview.progressPercent} />
              <p className="text-xs font-medium text-emerald-600">{overview.progressDeltaLabel}</p>
            </div>
          </article>

          <article className="rounded-2xl border border-secondary-200 bg-white p-4">
            <p className="text-sm font-medium text-secondary-600">Marcos</p>
            <div className="mt-3 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircleIcon className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-secondary-900">
                  {overview.milestonesDone} de {overview.milestonesTotal}
                </p>
                <p className="text-xs text-secondary-500">Marcos concluídos</p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary-100">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${milestonePercent}%` }} />
                </div>
              </div>
            </div>
          </article>

          <article className="rounded-2xl border border-secondary-200 bg-white p-4">
            <p className="text-sm font-medium text-secondary-600">Próxima entrega</p>
            <div className="mt-3 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                <CalendarDaysIcon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-semibold text-secondary-900">{overview.nextDeliveryDateLabel}</p>
                <p className="mt-0.5 text-xs text-secondary-500">{overview.nextDeliveryTitle}</p>
              </div>
            </div>
          </article>

          <article className="rounded-2xl border border-secondary-200 bg-white p-4">
            <p className="text-sm font-medium text-secondary-600">Aguardando você</p>
            <div className="mt-3 flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <UserIcon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <p className="text-sm font-semibold text-secondary-900">{overview.pendingCount} pendências</p>
                <p className="mt-0.5 text-xs text-secondary-500">{overview.pendingDetail}</p>
              </div>
            </div>
          </article>
        </div>

        <section className="rounded-2xl border border-secondary-200 bg-white p-5">
          <h2 className="text-base font-semibold text-secondary-900">Linha do tempo do projeto</h2>
          <ol className="mt-6 flex gap-0 overflow-x-auto pb-2">
            {overview.phases.map((phase, index) => {
              const isLast = index === overview.phases.length - 1;
              const lineDone = phase.status === 'done';
              const lineCurrent = phase.status === 'current';
              return (
                <li key={phase.id} className="relative flex min-w-[7.5rem] flex-1 flex-col items-center px-1">
                  {!isLast ? (
                    <span
                      className={`absolute left-[calc(50%+0.75rem)] right-[-0.25rem] top-3 h-0.5 ${
                        lineDone ? 'bg-emerald-400' : 'bg-secondary-200'
                      }`}
                      aria-hidden
                    />
                  ) : null}
                  <span
                    className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      phase.status === 'done'
                        ? 'border-emerald-500 bg-emerald-500 text-white'
                        : phase.status === 'current'
                          ? 'border-primary-500 bg-white text-primary-500'
                          : 'border-secondary-300 bg-white text-secondary-300'
                    }`}
                  >
                    {phase.status === 'done' ? (
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <span
                        className={`h-2 w-2 rounded-full ${
                          lineCurrent ? 'bg-primary-500' : 'bg-transparent'
                        }`}
                      />
                    )}
                  </span>
                  <p className="mt-3 text-center text-sm font-semibold text-secondary-900">{phase.name}</p>
                  <p
                    className={`mt-0.5 text-center text-xs font-medium ${
                      phase.status === 'done'
                        ? 'text-emerald-600'
                        : phase.status === 'current'
                          ? 'text-primary-700'
                          : 'text-secondary-500'
                    }`}
                  >
                    {phaseStatusLabel(phase.status)}
                  </p>
                  <p className="mt-0.5 text-center text-[11px] text-secondary-400">
                    {phase.startLabel} – {phase.endLabel}
                  </p>
                </li>
              );
            })}
          </ol>
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <section className="rounded-2xl border border-secondary-200 bg-white">
            <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
              <h2 className="text-base font-semibold text-secondary-900">Próximas entregas</h2>
              <Link to="?tab=deliveries" className="text-sm font-semibold text-primary-700 hover:underline">
                Ver todas
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="text-xs font-medium text-secondary-500">
                    <th className="px-5 py-3 font-medium">Entrega</th>
                    <th className="px-3 py-3 font-medium">Data prevista</th>
                    <th className="px-3 py-3 font-medium">Responsável</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {overview.deliveries.map((delivery) => (
                    <tr key={delivery.id} className="border-t border-secondary-100">
                      <td className="px-5 py-3 font-medium text-secondary-900">{delivery.title}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{delivery.dueLabel}</td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-2 text-secondary-700">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                            {initials(delivery.ownerName)}
                          </span>
                          <span className="whitespace-nowrap">{delivery.ownerName}</span>
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${deliveryStatusClass(delivery.status)}`}
                        >
                          {deliveryStatusLabel(delivery.status)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          className="rounded-lg p-1 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
                          aria-label="Mais opções"
                        >
                          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-2xl border border-secondary-200 bg-white">
            <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
              <h2 className="text-base font-semibold text-secondary-900">Atualizações recentes</h2>
              <button type="button" className="text-sm font-semibold text-primary-700 hover:underline">
                Ver todas
              </button>
            </div>
            <ul className="divide-y divide-secondary-100 px-5">
              {overview.updates.map((update) => (
                <li key={update.id} className="flex gap-3 py-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                    {initials(update.authorName)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-secondary-800">
                      <span className="font-semibold text-secondary-900">{update.authorName}</span>{' '}
                      <span className="text-secondary-600">{update.actionLabel}</span>{' '}
                      <span className="text-secondary-400">· {update.whenLabel}</span>
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-secondary-700">{update.body}</p>
                    {update.attachmentName ? (
                      <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
                        <PaperClipIcon className="h-4 w-4 text-secondary-500" aria-hidden />
                        <span className="font-medium">{update.attachmentName}</span>
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      <aside className="space-y-5">
        <section className="rounded-2xl border border-secondary-200 bg-white p-4">
          <h2 className="text-base font-semibold text-secondary-900">Ações rápidas</h2>
          <div className="mt-3 space-y-2">
            <button type="button" className="erp-btn-primary flex w-full items-center justify-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
              Enviar mensagem
            </button>
            <button type="button" className="erp-btn-secondary flex w-full items-center justify-center gap-2">
              <PlusIcon className="h-5 w-5" aria-hidden />
              Abrir solicitação
            </button>
            <button type="button" className="erp-btn-secondary flex w-full items-center justify-center gap-2">
              <DocumentTextIcon className="h-5 w-5" aria-hidden />
              Ver documentos
            </button>
            <Link to="?tab=planning" className="erp-btn-secondary flex w-full items-center justify-center gap-2">
              <CalendarDaysIcon className="h-5 w-5" aria-hidden />
              Ver cronograma
            </Link>
          </div>
        </section>

        <section className="rounded-2xl border border-secondary-200 bg-white p-4">
          <h2 className="text-base font-semibold text-secondary-900">Equipe do projeto</h2>
          <ul className="mt-4 space-y-3">
            {overview.team.map((member) => (
              <li key={member.id} className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-700">
                  {initials(member.name)}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-secondary-900">{member.name}</p>
                  <p className="truncate text-xs text-secondary-500">{member.role}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </aside>
    </div>
  );
}
